import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the DepartmentMasterContext
const DesignationMasterContext = createContext();

// Custom hook to use the DepartmentMasterContext
export const useDesignation = () => useContext(DesignationMasterContext);

// Provider component to wrap the app
export const DesignationMasterProvider = ({ children }) => {
  const [designations, setDesignations] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/masters/designations"; // Replace with your actual API URL
  const token = getToken();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDesignations = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setDepartments(response.data?.data || {}); // Access `data.data` for actual departments master
      const { data, current_page, last_page, per_page, total } = response.data;

      setDesignations(data || []); // Update leave types list
      setLoading(false);
    } catch (error) {
      console.error("Error fetching designations master:", error);
      setError(
        error.response?.data?.message || "Failed to fetch designations.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments master from the API on load
  useEffect(() => {
    fetchDesignations();
  }, [API_URL, token]);

  // Add a new department
  const addDesignation = async (newDesignation) => {
    try {
      const response = await axios.post(API_URL, newDesignation, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setDesignations((prev) => [...prev, response.data?.data]);
      setDesignations((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding designation:", error);
    }
  };

  // Update an existing designation
  const updateDesignation = async (updatedDesignation) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedDesignation.id}`,
        updatedDesignation,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setDesignations((prev) =>
        prev.map((designation) =>
          designation.id === updatedDesignation.id
            ? { ...designation, ...response.data?.data }
            : designation,
        ),
      );
    } catch (error) {
      console.error("Error updating designation master:", error);
    }
  };

  // Delete a designation
  const deleteDesignation = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDesignations((prev) =>
        prev.filter((designation) => designation.id !== id),
      );
    } catch (error) {
      console.error("Error deleting designation:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,

      designations,
      addDesignation,
      updateDesignation,
      deleteDesignation,
    }),
    [designations, isLoading], // Recalculate the value only when `designations` changes
  );

  return (
    <DesignationMasterContext.Provider value={value}>
      {children}
    </DesignationMasterContext.Provider>
  );
};
